import dynamic from "next/dynamic";
import { Box, Button, Container, Stack } from "@mui/material";
import { useContext, useEffect, useState } from "react";
const SearchBox = dynamic(() => import("../CommonComponents/SearchBox"), {
  ssr: false,
});
import { DronerCard } from "../DronerCard";
const SkillDroner = dynamic(() => import("./SkillDroner"), { ssr: false });
const Skills = dynamic(() => import("./Skills"), { ssr: false });
import { LocalizationContext } from "../../lib/context";
const TopDroners = dynamic(() => import("./TopDroners"), { ssr: false });
import { Maps } from "../Maps";
import { useScreenSize } from "../../hooks/useScreenSize";
import { DetailedSearchLargeView } from "@components/DetailedSearchLargeView";
import { SmallSearchView } from "@components/SmallSearchView";

const featuresData = [
  {
    skillId: "creative",
  },
  {
    skillId: "service",
  },
  {
    skillId: "survey and mapping",
  },
  {
    skillId: "agricultural",
  },
];

export default function SearchDroners({ droners }: any) {
  const { uiGeneral, detailedSearchPage } =
    useContext(LocalizationContext).localeStrings;
  const [sortedDroners, setSortedDroners] = useState(droners);
  const [activeSkill, setActiveSkill] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleDroners, setVisibleDroners] = useState<Array<any>>([]);
  const [dronersCount, setDronersCount] = useState<number>(0);
  const [loadedDroners, setLoadedDroners] = useState(16);
  const [sidebarDroners, setSidebarDroners] = useState<Array<any>>([]);
  const sm = useScreenSize("sm", "up");

  const [filters, setFilters] = useState({
    location: [],
    equipment: [],
    "free-range": 0,
    "max-range": 0,
    certification: [],
  });

  useEffect(() => {
    const sortDroners = (droners: any) => {
      const skills: any = {};
      skills["all"] = droners;
      for (const droner of droners) {
        if (droner?.skills && droner?.skills.length > 0) {
          const skill = droner.skills[0];
          if (skill && skills.hasOwnProperty(skill)) {
            skills[skill].push(droner);
          } else {
            skills[skill] = [droner];
          }
        }
      }
      delete skills.null;
      return skills;
    };
    setSortedDroners(sortDroners(droners));
  }, [droners]);

  useEffect(() => {
    const getFilters = () => {
      let locations: string[] = [];
      let certificates: string[] = [];
      let equipments: string[] = [];
      for (const filter in filters) {
        if (filter == "location") {
          locations = Object.values(filters[filter]).map((f: any) => f.value);
        } else if (filter == "certification") {
          certificates = Object.values(filters[filter]).map(
            (f: any) => f.value
          );
        } else if (filter == "equipment") {
          equipments = Object.values(filters[filter]).map(
            (f: any) => f.translation
          );
        }
      }
      return {
        locations,
        certificates,
        equipments,
        maxRange: filters["max-range"],
        freeRange: filters["free-range"],
      };
    };
    const { locations, certificates, equipments, maxRange, freeRange } =
      getFilters();

    let completeFilteredDroners = droners;

    if (activeSkill != "all") {
      completeFilteredDroners = sortedDroners[activeSkill];
    }

    completeFilteredDroners = completeFilteredDroners.filter((droner: any) => {
      if (maxRange) {
        if (!(maxRange >= droner.maxRange)) return false;
      }

      if (freeRange) {
        if (!(freeRange >= droner.freeDist)) return false;
      }

      if (!locations.length && !certificates.length && !equipments.length)
        return true;

      if (locations.length > 0) {
        for (const location of locations) {
          /*
            location is a string that can be the name of a country or the name of a city, country
            So if the name was only the country without city search for the droner.countryObject.long_name if it matches the location return true
            If the name was the city, country search for the droner.city if it matches the location and return true
           */
          const place = location.split(",");
          if (place.length > 1) {
            if (droner?.city?.includes(place[0])) return true;
          } else {
            if (droner?.countryObject?.long_name?.includes(location)) {
              return true;
            }
          }
        }
      }

      if (equipments.length > 0) {
        for (const equipment of equipments) {
          const matchEquipment = droner.equipment.some(
            (dEquipment: string) => equipment == dEquipment
          );
          if (matchEquipment) return true;
        }
      }
      if (certificates.length > 0) {
        for (const certificate of certificates) {
          const matchCertificate = droner.certificate?.some(
            (dCert: string) => certificate == dCert
          );
          if (matchCertificate) return true;
        }
      }

      return false;
    });

    if (searchQuery !== "") {
      completeFilteredDroners = completeFilteredDroners.filter((droner: any) =>
        droner?.displayName
          ?.toLowerCase()
          .includes(searchQuery.toLocaleLowerCase())
      );
    }

    setVisibleDroners(completeFilteredDroners);
    setDronersCount(completeFilteredDroners.length);
  }, [
    droners,
    filters,
    loadedDroners,
    searchQuery,
    activeSkill,
    sortedDroners,
  ]);

  return (
    <>
      <Container
        sx={{ overflowX: "scroll", mt: 3, position: "absolute", zIndex: "2" }}
        id="all-jobs"
      >
        <Skills
          skills={sortedDroners}
          setActiveSkill={setActiveSkill}
          containerStyle={{ justifyContent: { sm: "center" } }}
          boxStyle={{ boxShadow: "5px 5px 10px #888" }}
        />
      </Container>

      <Box
        className="p-absolute top-none w-full h-full"
        sx={{ left: 0, top: 0 }}
        id="maps"
      >
        <Maps
          droners={visibleDroners}
          mobileListHeight={100}
          setSidebarDroners={setSidebarDroners}
        />
      </Box>
      {sm && (
        <DetailedSearchLargeView
          selectedDroners={sidebarDroners}
          setSelectedDroners={setSidebarDroners}
          localizedPageContent={detailedSearchPage}
        />
      )}
      {!sm && (
        <SmallSearchView
          selectedDroners={sidebarDroners}
          localizedPageContent={detailedSearchPage}
          drawerBleeding={0}
        />
      )}

      <Box sx={{ mt: "calc(95vh)", mb: 4 }}>
        <SearchBox
          setFilter={setFilters}
          filter={filters}
          setQuery={setSearchQuery}
        />
      </Box>

      <Container
        className="flex col gap-normal"
        sx={{
          maxWidth: "1248px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {visibleDroners?.length > 0 ? (
            visibleDroners
              ?.slice(0, loadedDroners)
              ?.map((droner: any, i: number) => (
                <DronerCard key={i} droner={droner}></DronerCard>
              ))
          ) : (
            <h5 className="no-discounts">
              No droners found for the given filters or search query!
            </h5>
          )}
        </Box>

        <Stack sx={{ mt: 5 }}>
          <Button
            size="large"
            variant="contained"
            onClick={() => setLoadedDroners(loadedDroners + 16)}
            sx={{ mx: "auto" }}
            disabled={loadedDroners >= dronersCount}
          >
            Load more
          </Button>
        </Stack>

        <Box className="pb-huge" />

        <TopDroners droners={droners} />

        {Object.keys(sortedDroners).length > 0
          ? Object.entries(sortedDroners).map((skill) => {
              const localizedSkillName =
                skill[0] === "all"
                  ? uiGeneral.all
                  : uiGeneral.skills.filter(
                      (s) => s.skillId === skill[0].toLowerCase()
                    )[0]?.name;
              return (
                <Box key={skill[0]} sx={{ mb: "51px" }}>
                  {skill[0] != "all" ? (
                    <SkillDroner
                      title={localizedSkillName ?? ""}
                      droners={skill[1]}
                    />
                  ) : null}
                </Box>
              );
            })
          : null}
      </Container>
    </>
  );
}
